import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import { Heart, MessageCircle, Send, Star, Users, UserPlus, X, Search } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Community({ groupId = null }) {
  const { lang, user } = useApp();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audience, setAudience] = useState("all"); // "all" | "friends"
  const [friendsDialogOpen, setFriendsDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = groupId ? { group_id: groupId } : { audience };
      const res = await api.get("/community/posts", { params });
      setPosts(res.data);
    } finally {
      setLoading(false);
    }
  }, [groupId, audience]);

  useEffect(() => { load(); }, [load]);

  const toggleLike = async (p) => {
    const res = await api.post(`/community/posts/${p.post_id}/like`);
    setPosts((prev) =>
      prev.map((x) =>
        x.post_id === p.post_id ? { ...x, liked_by_me: res.data.liked_by_me, likes_count: res.data.likes_count } : x
      )
    );
  };

  return (
    <div>
      {!groupId && (
        <>
          <p className="uppercase tracking-[0.3em] text-xs text-[var(--text-secondary)]">FaithCycle</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">
            {t("communityFeed", lang)}
          </h1>
          <p className="mt-3 text-[var(--text-secondary)] text-sm">
            {t("newestFirst", lang)}
          </p>

          {/* Audience tabs */}
          <div className="mt-8 flex items-center gap-2 border-b border-[var(--border)]">
            <TabBtn active={audience === "all"} onClick={() => setAudience("all")} testid="tab-all">
              {lang === "es" ? "Comunidad" : "Community"}
            </TabBtn>
            <TabBtn active={audience === "friends"} onClick={() => setAudience("friends")} testid="tab-friends">
              {lang === "es" ? "Amistades" : "Friends"}
            </TabBtn>
            <button
              data-testid="manage-friends-btn"
              onClick={() => setFriendsDialogOpen(true)}
              className="ml-auto mb-2 text-xs uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1"
            >
              <UserPlus className="w-3.5 h-3.5" strokeWidth={1.5} />
              {lang === "es" ? "Amigos" : "Friends"}
            </button>
          </div>
        </>
      )}

      <div className={`${groupId ? "" : "mt-10"} max-w-2xl space-y-6`}>
        {loading && <p className="text-[var(--text-secondary)]">{t("loading", lang)}</p>}
        {!loading && posts.length === 0 && (
          <p className="text-[var(--text-secondary)]">
            {audience === "friends"
              ? (lang === "es"
                  ? "Aún no tienes amistades con publicaciones. Añade personas desde el botón \"Amigos\"."
                  : 'No posts from friends yet. Add people using the "Friends" button.')
              : t("emptyFeed", lang)}
          </p>
        )}
        {posts.map((p) => (
          <PostCard key={p.post_id} post={p} onLike={toggleLike} lang={lang} onOpenVerse={() => navigate(`/read?b=${p.book}&c=${p.chapter}&v=${p.verse}`)} currentUser={user} />
        ))}
      </div>

      <FriendsDialog
        open={friendsDialogOpen}
        onClose={() => setFriendsDialogOpen(false)}
        lang={lang}
        onChanged={() => audience === "friends" && load()}
      />
    </div>
  );
}

function FriendsDialog({ open, onClose, lang, onChanged }) {
  const [friends, setFriends] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadFriends = useCallback(async () => {
    try {
      const r = await api.get("/friends");
      setFriends(r.data);
    } catch {}
  }, []);

  useEffect(() => { if (open) loadFriends(); }, [open, loadFriends]);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    let cancel = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/users/search", { params: { q } });
        if (!cancel) setResults(r.data);
      } catch {} finally { if (!cancel) setLoading(false); }
    }, 300);
    return () => { cancel = true; clearTimeout(t); };
  }, [q]);

  const addFriend = async (u) => {
    try {
      await api.post("/friends/add", { friend_user_id: u.user_id });
      toast.success(lang === "es" ? `${u.name} añadido como amigo` : `${u.name} added`);
      await loadFriends();
      setResults((prev) => prev.map((r) => (r.user_id === u.user_id ? { ...r, is_friend: true } : r)));
      onChanged?.();
    } catch { toast.error(lang === "es" ? "No se pudo añadir" : "Could not add"); }
  };

  const removeFriend = async (u) => {
    try {
      await api.delete(`/friends/${u.user_id}`);
      toast.success(lang === "es" ? "Amigo eliminado" : "Friend removed");
      await loadFriends();
      setResults((prev) => prev.map((r) => (r.user_id === u.user_id ? { ...r, is_friend: false } : r)));
      onChanged?.();
    } catch { toast.error(lang === "es" ? "No se pudo eliminar" : "Could not remove"); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="friends-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[85vh] flex flex-col bg-[var(--surface-elevated)] border border-[var(--border)] rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl tracking-tight">{lang === "es" ? "Amistades" : "Friends"}</h2>
          </div>
          <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--accent)]">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" strokeWidth={1.5} />
            <Input
              data-testid="friends-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={lang === "es" ? "Buscar por nombre o correo…" : "Search by name or email…"}
              className="pl-9 h-11 bg-[var(--bg)] border-[var(--border)]"
            />
          </div>

          {q.length >= 2 && (
            <div className="space-y-2">
              <p className="uppercase tracking-widest text-[10px] text-[var(--text-secondary)]">
                {lang === "es" ? "Resultados" : "Results"}
              </p>
              {loading && <p className="text-sm text-[var(--text-secondary)]">…</p>}
              {!loading && results.length === 0 && (
                <p className="text-sm text-[var(--text-secondary)]">{lang === "es" ? "Sin resultados" : "No results"}</p>
              )}
              {results.map((u) => (
                <PersonRow key={u.user_id} u={u} lang={lang} onAdd={addFriend} onRemove={removeFriend} isFriend={u.is_friend} />
              ))}
            </div>
          )}

          <div className="pt-2 border-t border-[var(--border)]">
            <p className="uppercase tracking-widest text-[10px] text-[var(--text-secondary)] mb-2">
              {lang === "es" ? "Mis amistades" : "My friends"} ({friends.length})
            </p>
            {friends.length === 0 && (
              <p className="text-sm text-[var(--text-secondary)]">
                {lang === "es" ? "Aún no tienes amistades. Busca personas arriba." : "No friends yet. Search above."}
              </p>
            )}
            {friends.map((u) => (
              <PersonRow key={u.user_id} u={u} lang={lang} isFriend={true} onRemove={removeFriend} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PersonRow({ u, lang, onAdd, onRemove, isFriend }) {
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`person-row-${u.user_id}`}>
      <Avatar className="w-9 h-9 border border-[var(--border)]">
        <AvatarImage src={u.picture} alt={u.name} />
        <AvatarFallback className="text-xs bg-[var(--surface)]">{u.name?.[0]?.toUpperCase() || "?"}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate flex items-center gap-1">
          {u.name}
          {u.is_supporter && <Star className="w-3 h-3 text-[var(--accent)] fill-[var(--accent)]" strokeWidth={1.5} />}
        </p>
        <p className="text-xs text-[var(--text-secondary)] truncate">{u.email}</p>
      </div>
      {isFriend ? (
        <button
          onClick={() => onRemove(u)}
          data-testid={`remove-friend-${u.user_id}`}
          className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] hover:text-red-500 transition-colors px-3 py-1"
        >
          {lang === "es" ? "Quitar" : "Remove"}
        </button>
      ) : (
        <button
          onClick={() => onAdd(u)}
          data-testid={`add-friend-${u.user_id}`}
          className="text-[10px] uppercase tracking-widest text-white bg-[var(--accent)] hover:opacity-90 transition-opacity rounded-full px-3 py-1.5 flex items-center gap-1"
        >
          <UserPlus className="w-3 h-3" strokeWidth={1.75} />
          {lang === "es" ? "Añadir" : "Add"}
        </button>
      )}
    </div>
  );
}

function PostCard({ post, onLike, lang, onOpenVerse, currentUser }) {
  const [comments, setComments] = useState([]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [count, setCount] = useState(post.comments_count);

  const load = async () => {
    const res = await api.get(`/community/posts/${post.post_id}/comments`);
    setComments(res.data);
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && comments.length === 0) await load();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const res = await api.post(`/community/posts/${post.post_id}/comments`, { text: text.trim() });
    setComments((c) => [...c, res.data]);
    setCount((n) => n + 1);
    setText("");
  };

  return (
    <article data-testid={`post-${post.post_id}`} className="rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-6">
      <header className="flex items-center gap-3">
        <Avatar className="w-10 h-10">
          <AvatarImage src={post.user_picture} alt={post.user_name} />
          <AvatarFallback className="bg-[var(--surface)] text-sm">
            {post.user_name?.[0]?.toUpperCase() || "?"}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sm flex items-center gap-1.5">
            {post.user_name}
            {post.user_is_supporter && (
              <Star className="w-3 h-3 text-[var(--accent)] fill-[var(--accent)]" strokeWidth={1.5} />
            )}
          </p>
          <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">
            {new Date(post.created_at).toLocaleDateString()}
          </p>
        </div>
      </header>

      <button
        onClick={onOpenVerse}
        className="mt-5 block w-full text-left rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 hover:bg-[var(--highlight)]/50 transition-colors duration-200"
      >
        <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)]">
          {post.book_name || ""} {post.chapter}:{post.verse} · {post.translation}
        </p>
        <p className="mt-2 font-serif-body text-base leading-relaxed">“{post.verse_text}”</p>
      </button>

      <p className="mt-5 leading-relaxed text-[var(--text-primary)]">{post.reflection}</p>

      <div className="mt-5 flex items-center gap-6 text-xs uppercase tracking-widest text-[var(--text-secondary)]">
        <button
          data-testid={`like-btn-${post.post_id}`}
          onClick={() => onLike(post)}
          className="flex items-center gap-2 hover:text-[var(--accent)] transition-colors duration-200"
        >
          <Heart
            className={`w-4 h-4 ${post.liked_by_me ? "fill-[var(--accent)] text-[var(--accent)]" : ""}`}
            strokeWidth={1.5}
          />
          <span>{post.likes_count}</span>
        </button>
        <button
          data-testid={`comments-toggle-${post.post_id}`}
          onClick={toggle}
          className="flex items-center gap-2 hover:text-[var(--accent)] transition-colors duration-200"
        >
          <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
          <span>{count}</span>
        </button>
      </div>

      {open && (
        <div className="mt-5 pt-5 border-t border-[var(--border)] space-y-4">
          {comments.map((c) => (
            <div key={c.comment_id} className="flex items-start gap-3">
              <Avatar className="w-7 h-7">
                <AvatarImage src={c.user_picture} alt={c.user_name} />
                <AvatarFallback className="text-xs bg-[var(--surface)]">
                  {c.user_name?.[0]?.toUpperCase() || "?"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-xs text-[var(--text-secondary)] flex items-center gap-1">
                  {c.user_name}
                  {c.user_is_supporter && (
                    <Star className="w-3 h-3 text-[var(--accent)] fill-[var(--accent)]" strokeWidth={1.5} />
                  )}
                </p>
                <p className="text-sm mt-0.5">{c.text}</p>
              </div>
            </div>
          ))}
          <form onSubmit={submit} className="flex items-center gap-2">
            <Input
              data-testid={`comment-input-${post.post_id}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("addComment", lang)}
              className="h-10 rounded-full bg-[var(--bg)] border-[var(--border)]"
            />
            <button
              data-testid={`comment-submit-${post.post_id}`}
              type="submit"
              className="h-10 w-10 rounded-full flex items-center justify-center bg-[var(--accent)] text-white hover:opacity-90 transition-opacity duration-200"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </form>
        </div>
      )}
    </article>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-3 text-sm uppercase tracking-widest transition-colors duration-200 border-b-2 -mb-px ${
        active
          ? "border-[var(--accent)] text-[var(--text-primary)]"
          : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      }`}
    >
      {children}
    </button>
  );
}
